import { useQuery } from '@tanstack/react-query';
import { isOfflineGap } from '@/offline/availability';
import { formatDateTime, roundMoney } from '@/format';
import { company } from '@/stores/company';
import { Fragment, useState, type ReactNode } from 'react';
import { api, type GetPaths } from '@/api/client';
import { useAuth } from '@/stores/auth';
import { AppIcon, type IconKind } from './AppIcon';
import { ErrorNote, Field, Modal, Table, money, quantity, rate } from './ui';
import { useItemsById } from '@/hooks/inventory';
import { packedBreakdown } from '@/packing';

/**
 * Shared pieces for the four inventory document types (inward, consumption,
 * transfers, adjustments): a per-document audit timeline, printing, and a
 * reverse action that collects its mandatory reason.
 *
 * Data is never exported as a file; people print what they need.
 *
 * The API does not declare a JSON response schema for the history route — a
 * timeline is a small, stable shape, so it is typed by hand here.
 */

export interface HistoryEntry {
  id: string;
  action: string;
  actorId: string | null;
  actorUserId: string | null;
  actorName: string | null;
  actorRole: string | null;
  before: unknown;
  after: unknown;
  detail: string | null;
  createdAt: string;
}

/**
 * This entity's own audit timeline, newest first — the last thing that
 * happened is the first line. `path` is the exact `.../:id/history` route for
 * the document in question.
 *
 * Whose entries come back is the server's decision, from the caller's
 * permissions: without `iam.history.read` a person is shown only what they
 * did themselves. The panel says so, since a timeline of one line would
 * otherwise read as "nothing else happened".
 */
export function HistoryPanel({ path }: { path: string }) {
  const { can } = useAuth();
  const everyone = can('iam.history.read');
  const history = useQuery({
    queryKey: ['history', path],
    queryFn: async () => (await api.get(path as GetPaths)) as unknown as HistoryEntry[],
  });
  const { data, isPending, isError, error } = history;

  if (isOfflineGap(history)) return <p className="muted">History was not loaded on this PC. It needs the connection.</p>;
  if (isPending) return <p className="muted">Loading history…</p>;
  if (isError)
    return <ErrorNote message={error instanceof Error ? error.message : 'Could not load history'} />;

  return (
    <div className="stack">
      <Table
        count={data.length}
        empty={everyone ? 'No history recorded yet.' : 'You have not done anything to this record yourself.'}
        head={
          <tr>
            <th>When</th>
            <th>Action</th>
            <th>By</th>
            <th>Detail</th>
          </tr>
        }
      >
        {data.map((h) => (
          <tr key={h.id}>
            <td className="dim">{formatDateTime(h.createdAt)}</td>
            <td>
              <span className="pill">{h.action}</span>
            </td>
            <td>{h.actorName ?? <span className="dim">System</span>}</td>
            <td className="dim">{h.detail ?? '—'}</td>
          </tr>
        ))}
      </Table>
      {!everyone && <p className="muted">Your own entries. Seeing everyone's is a separate permission.</p>}
    </div>
  );
}

/** A document formatted plainly and sent to the browser's own print dialog —
 *  no server round trip, since everything needed is already on screen. */
export function printDocument(doc: {
  title: string;
  facts: Array<[string, string]>;
  lines?: Array<{
    item: string;
    qty: string;
    unitCost?: string;
    value?: string;
    /** The line's value as a plain number, for group subtotals. */
    amount?: string;
    lot?: string;
    /** Listed together under this heading, with a subtotal when costs show. */
    group?: string;
  }>;
  showCost: boolean;
}): void {
  const esc = escapeHtml;
  const factsHtml = doc.facts
    .map(([label, value]) => `<tr><th>${esc(label)}</th><td>${esc(value)}</td></tr>`)
    .join('');
  const linesHtml = doc.lines
    ? `<h2>Lines</h2><table class="lines"><thead><tr><th>Item</th><th>Qty</th>${
        doc.showCost ? '<th>Unit cost</th><th>Value</th>' : ''
      }<th>Lot</th></tr></thead><tbody>${grouped(doc.lines, (l) => l.amount)
        .map(
          (run) =>
            (run.group !== undefined
              ? `<tr><th colspan="${doc.showCost ? 5 : 3}">${esc(run.group)}</th></tr>`
              : '') +
            run.lines
              .map(
                (l) =>
                  `<tr><td>${esc(l.item)}</td><td>${esc(l.qty)}</td>${
                    doc.showCost ? `<td>${esc(l.unitCost ?? '')}</td><td>${esc(l.value ?? '')}</td>` : ''
                  }<td>${esc(l.lot ?? '—')}</td></tr>`,
              )
              .join('') +
            (run.group !== undefined && doc.showCost
              ? `<tr><td colspan="3"><strong>Subtotal</strong></td><td><strong>${esc(money(run.subtotal))}</strong></td><td></td></tr>`
              : ''),
        )
        .join('')}</tbody></table>`
    : '';

  openPrintWindow({
    title: doc.title,
    css: `
    table.facts th { width: 12rem; background: #f5f5f5; }
    table.lines th { background: #f5f5f5; }`,
    body: `<h1>${esc(doc.title)}</h1>
    <table class="facts">${factsHtml}</table>
    ${linesHtml}`,
  });
}

/** Escapes text for the print window's HTML. */
export function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Opens a print window with the company letterhead and footer around `body`,
 * and hands it to the browser's print dialog. Every printout — documents,
 * payslips, pay sheets, reports — goes through here so they all look alike.
 * `page` sets the paper: A4 (default), A5 for a single payslip, or A4 turned
 * sideways for wide sheets.
 */
export function openPrintWindow(doc: {
  title: string;
  body: string;
  css?: string;
  page?: 'A4' | 'A5' | 'A4 landscape';
}): void {
  const esc = escapeHtml;
  // Not "noopener": with it, window.open returns null by specification, so
  // there is no window to write into and Print silently did nothing. The
  // window is a blank page this code fills itself, and the back-link is cut
  // by hand instead.
  const w = window.open('', '_blank', 'width=900,height=900');
  if (!w) return;
  w.opener = null;

  // The letterhead and footer come from Settings › Company. The logo is only
  // ever a PNG or JPEG data URL (the server checks); anything else is left out.
  const c = company();
  const logo = c.logoDataUrl && /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/.test(c.logoDataUrl)
    ? `<img class="logo" src="${c.logoDataUrl}" alt="">`
    : '';
  const contact = [c.address, c.phone, c.email, c.taxNumber ? `Tax number: ${c.taxNumber}` : null]
    .filter((x): x is string => Boolean(x))
    .map(esc)
    .join(' · ');
  const letterhead = `<header class="letterhead">${logo}<div><div class="company">${esc(c.nameEn)}</div>${
    c.nameUr ? `<div class="company-ur" dir="rtl" lang="ur">${esc(c.nameUr)}</div>` : ''
  }${contact ? `<div class="contact">${contact}</div>` : ''}</div></header>`;
  const footer = `<footer>${c.printFooter ? `<p>${esc(c.printFooter)}</p>` : ''}<p>Printed ${esc(
    formatDateTime(new Date()),
  )}</p></footer>`;

  w.document.write(`<!doctype html><html><head><title>${esc(doc.title)}</title><style>
    body { font-family: system-ui, sans-serif; color: #111; padding: 1.5rem; }
    h1 { font-size: 1.3rem; margin-block-end: 1rem; }
    h2 { font-size: 1rem; margin-block: 1.5rem 0.5rem; }
    table { border-collapse: collapse; width: 100%; }
    th, td { border: 1px solid #ccc; padding: 0.35rem 0.6rem; text-align: left; font-size: 0.9rem; }
    .num { text-align: right; font-variant-numeric: tabular-nums; }
    .urdu { font-family: 'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', serif; }
    .letterhead { display: flex; align-items: center; gap: 1rem; padding-block-end: 0.75rem; margin-block-end: 1rem; border-bottom: 2px solid #111; }
    .letterhead .logo { max-height: 56px; max-width: 160px; }
    .letterhead .company { font-size: 1.2rem; font-weight: 700; }
    .letterhead .company-ur { font-size: 1rem; }
    .letterhead .contact { font-size: 0.8rem; color: #444; margin-top: 0.2rem; }
    footer { margin-top: 2rem; padding-top: 0.5rem; border-top: 1px solid #ccc; font-size: 0.75rem; color: #444; }
    footer p { margin: 0.2rem 0; }
    .pagebreak { break-after: page; }
    @page { size: ${doc.page ?? 'A4'}; margin: 12mm; }
    ${doc.css ?? ''}
  </style></head><body>
    ${letterhead}
    ${doc.body}
    ${footer}
  </body></html>`);
  w.document.close();
  w.focus();
  w.print();
}

/** A button that opens a modal collecting a mandatory reason, then fires
 *  `onReverse` with it. Every reversal route requires a reason, so the
 *  prompt is not optional the way a plain confirm() dialog would suggest. */
export function ReverseButton({
  onReverse,
  pending,
  label = 'Reverse',
}: {
  onReverse: (reason: string) => void;
  pending?: boolean;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  return (
    <>
      <button type="button" className="btn btn--withicon" onClick={() => setOpen(true)}>
        <AppIcon kind="reverse" size={15} />
        {label}
      </button>
      {open && (
        <Modal title="Reverse this document" onClose={() => setOpen(false)}>
          <h2 className="withicon">
            <AppIcon kind="reverse" size={18} />
            Reverse this document
          </h2>
          <p className="muted settings__intro">
            This undoes its effect on the stock ledger with a mirror entry — it does not delete anything. The
            reason is the whole record of why.
          </p>
          <Field
            label="Reason"
            required
            value={reason}
            onChange={setReason}
            placeholder="Damaged in transit — supplier already credited us"
          />
          <div className="toolbar">
            <button
              type="button"
              className="btn btn--primary"
              disabled={!reason.trim() || pending}
              onClick={() => {
                onReverse(reason.trim());
                setOpen(false);
                setReason('');
              }}
            >
              {pending ? 'Reversing…' : 'Confirm reversal'}
            </button>
            <button type="button" className="btn" onClick={() => setOpen(false)}>
              Cancel
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

/** A plain confirm-guarded delete button. The version a draft delete needs
 *  for its own CAS check is the caller's concern — it already has the row
 *  being deleted, and therefore its version, from the list it rendered. */
export function DeleteButton({
  onDelete,
  pending,
  label = 'Delete',
  confirmText = 'Delete this draft? This cannot be undone.',
}: {
  onDelete: () => void;
  pending?: boolean;
  label?: string;
  confirmText?: string;
}) {
  return (
    <button
      type="button"
      className="btn btn--withicon"
      disabled={pending}
      onClick={() => {
        if (window.confirm(confirmText)) onDelete();
      }}
    >
      <AppIcon kind="remove" size={15} />
      {pending ? 'Deleting…' : label}
    </button>
  );
}

export interface ReviewLine {
  item: string;
  qty: string;
  unitCost?: string | undefined;
  value?: string | undefined;
  lot?: string | null | undefined;
  /** Lines sharing a group (a stock-in's supplier, say) are listed together
   *  under its heading, with a subtotal when costs show. */
  group?: string | undefined;
}

/** Lines in the order given, cut wherever their group changes. Lines with
 *  no group form one unnamed run. */
function grouped<T extends { group?: string | undefined }>(
  lines: readonly T[],
  /** The line's value as a plain number, for the subtotal. */
  amountOf: (line: T) => string | undefined,
): Array<{ group: string | undefined; lines: T[]; subtotal: string }> {
  const runs: Array<{ group: string | undefined; lines: T[]; subtotal: string }> = [];
  for (const line of lines) {
    const last = runs[runs.length - 1];
    if (last && last.group === line.group) last.lines.push(line);
    else runs.push({ group: line.group, lines: [line], subtotal: '0' });
  }
  for (const run of runs) {
    run.subtotal = roundMoney(run.lines.reduce((sum, l) => sum + (Number(amountOf(l)) || 0), 0)).toFixed(2);
  }
  return runs;
}

/**
 * A read-only summary of everything a compose screen has staged, shown
 * before anything is actually saved — the moment to catch a wrong store or a
 * missing item, rather than after the request is already in flight. The
 * real save/post actions live here, not on the compose screen itself.
 */
export function ReviewSummaryDialog({
  title,
  icon,
  facts,
  lines,
  showCost = false,
  note,
  onClose,
  primary,
  secondary,
}: {
  title: string;
  /** The document type's icon, drawn before the heading. */
  icon?: IconKind;
  facts: Array<[string, string]>;
  lines: ReviewLine[];
  showCost?: boolean;
  /** A line under the items explaining anything the save will do that the
   *  table alone does not make obvious. */
  note?: string | undefined;
  onClose: () => void;
  primary: { label: string; pending?: boolean; onClick: () => void };
  secondary?: { label: string; pending?: boolean; onClick: () => void } | undefined;
}) {
  return (
    <Modal title={title} onClose={onClose}>
      <h2 className={icon ? 'withicon' : undefined}>
        {icon && <AppIcon kind={icon} size={18} />}
        {title}
      </h2>
      <dl className="settings__facts">
        {facts.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>

      <h3 className="formsection">Items</h3>
      <Table
        count={lines.length}
        empty="No items."
        head={
          <tr>
            <th>Item</th>
            <th className="num">Quantity</th>
            {showCost && <th className="num">Unit cost</th>}
            {showCost && <th className="num">Value</th>}
            <th>Lot</th>
          </tr>
        }
      >
        {grouped(lines, (l) => l.value).map((run, r) => (
          <Fragment key={r}>
            {run.group !== undefined && (
              <tr className="table__group">
                <th colSpan={showCost ? 5 : 3} scope="colgroup">
                  {run.group}
                </th>
              </tr>
            )}
            {run.lines.map((l, i) => (
              <tr key={i}>
                <td>{l.item}</td>
                <td className="num">{l.qty}</td>
                {showCost && <td className="num">{l.unitCost ? rate(l.unitCost) : '—'}</td>}
                {showCost && <td className="num">{l.value ? money(l.value) : '—'}</td>}
                <td className="dim">{l.lot ?? '—'}</td>
              </tr>
            ))}
            {run.group !== undefined && showCost && (
              <tr className="table__subtotal">
                <td colSpan={3}>Subtotal</td>
                <td className="num">{money(run.subtotal)}</td>
                <td />
              </tr>
            )}
          </Fragment>
        ))}
      </Table>
      {note && <p className="muted">{note}</p>}

      <div className="toolbar">
        <button
          type="button"
          className="btn btn--primary"
          disabled={primary.pending}
          onClick={primary.onClick}
        >
          {primary.pending ? 'Saving…' : primary.label}
        </button>
        {secondary && (
          <button type="button" className="btn" disabled={secondary.pending} onClick={secondary.onClick}>
            {secondary.pending ? 'Saving…' : secondary.label}
          </button>
        )}
        <button type="button" className="btn btn--ghost" onClick={onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}

export interface DocumentLineRow {
  id: string;
  skuCode: string;
  skuName: string;
  qty: string;
  unitCost: string;
  value: string;
  batchNo: string | null;
}

/**
 * A document's own detail view: facts, its lines, a Details/History tab
 * switch, and whatever actions (edit, delete, post, reverse) the caller
 * decides apply to this row's status and the viewer's permissions.
 *
 * Generic across the four document types — each screen maps its own detail
 * response into `facts` and `lines` rather than this component knowing the
 * shape of an inward shipment versus a transfer.
 */
export function DocumentDetailModal({
  title,
  onClose,
  facts,
  lines,
  showCost,
  historyPath,
  actions,
}: {
  title: string;
  onClose: () => void;
  facts: Array<{ label: string; value: ReactNode }>;
  lines?: DocumentLineRow[];
  showCost: boolean;
  historyPath: string;
  actions?: ReactNode;
}) {
  const [tab, setTab] = useState<'details' | 'history'>('details');
  // The lines name items by code: their packages, to show each quantity in them.
  const itemsById = useItemsById();
  const byCode = new Map([...itemsById.values()].map((i) => [i.code, i]));

  return (
    <Modal title={title} onClose={onClose}>
      <h2>{title}</h2>

      <div className="toolbar">
        <button
          type="button"
          className={`btn ${tab === 'details' ? 'btn--primary' : ''}`}
          onClick={() => setTab('details')}
        >
          Details
        </button>
        <button
          type="button"
          className={`btn ${tab === 'history' ? 'btn--primary' : ''}`}
          onClick={() => setTab('history')}
        >
          History
        </button>
      </div>

      {tab === 'details' && (
        <div className="stack">
          <dl className="settings__facts">
            {facts.map((f, i) => (
              <div key={i}>
                <dt>{f.label}</dt>
                <dd>{f.value}</dd>
              </div>
            ))}
          </dl>

          {lines && (
            <>
              <h3 className="formsection">Lines</h3>
              <Table
                count={lines.length}
                empty="No lines."
                head={
                  <tr>
                    <th>Item</th>
                    <th className="num">Quantity</th>
                    {showCost && <th className="num">Unit cost</th>}
                    {showCost && <th className="num">Value</th>}
                    <th>Lot</th>
                  </tr>
                }
              >
                {lines.map((l) => (
                  <tr key={l.id}>
                    <td>
                      <span className="mono">{l.skuCode}</span> {l.skuName}
                    </td>
                    <td className="num">
                      {quantity(l.qty, 4)}
                      {byCode.get(l.skuCode) && packedBreakdown(l.qty, byCode.get(l.skuCode)!) && (
                        <div className="dim">{packedBreakdown(l.qty, byCode.get(l.skuCode)!)}</div>
                      )}
                    </td>
                    {showCost && <td className="num">{rate(l.unitCost)}</td>}
                    {showCost && <td className="num">{money(l.value)}</td>}
                    <td className="dim">{l.batchNo ?? '—'}</td>
                  </tr>
                ))}
              </Table>
            </>
          )}

          {actions && <div className="toolbar">{actions}</div>}
        </div>
      )}

      {tab === 'history' && <HistoryPanel path={historyPath} />}
    </Modal>
  );
}
